import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import FloatingInstructions from "../../components/FloatingInstructions/FloatingInstructions";
import API_BASE_URL from "../../utils/api";
import { useAuth } from "../../components/Context/AuthContext";
import DownloadIcon from "@mui/icons-material/Download";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
const PageItem = ({
  page,
  documentId,
  navigate,
  isAdmin,
  onContentTypeChange,
}) => {
  const getStatusText = (status) => {
    if (status === "completed") return "Completed";
    if (status === "in review") return "Review";
    return "Transcribe";
  };

  const getStatusIcon = (status) => {
    if (status === "completed") return <DoneOutlineIcon />;
    if (status === "in review") return <SearchIcon />;
    return <EditNoteIcon />;
  };
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const loadSaved = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) return;

        const res = await axios.get(
          `${API_BASE_URL}/user-activity/favorite/${documentId}/${page.pageNumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSaved(res.data.isFavorite === true);
      } catch (err) {
        console.error("Favorite check failed:", err);
      }
    };

    loadSaved();
  }, [documentId, page.pageNumber]);
  const toggleSaved = async () => {
    try {
      const next = !saved;

      const res = await axios.patch(
        `${API_BASE_URL}/user-activity/favorite/${documentId}/${page.pageNumber}`,
        {
          isFavorite: next,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSaved(res.data.isFavorite === true);
    } catch (err) {
      console.error("Favorite update failed:", err);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: "10px",
        padding: "10px",
        textAlign: "center",
        width: "250px",
      }}
    >
      {" "}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 1,
        }}
      >
        {getStatusIcon(page.status)}{" "}
        <Typography variant="body1" sx={{}}>
          {getStatusText(page.status)}{" "}
        </Typography>{" "}
      </Box>{" "}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "3/4",
          border: "1px solid #ccc",
          marginBottom: "10px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f9f9f9",
          borderRadius: "10px",
        }}
      >
        {" "}
        <img
          src={`${page.imageUrl}`}
          alt={`Page ${page.pageNumber}`}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />{" "}
      </Box>
      <Typography variant="body2">Page {page.pageNumber}</Typography>{" "}
      {page.qualityFlags?.flaggedForAttention && (
        <Chip
          label="Needs Attention"
          color="warning"
          size="small"
          sx={{ mt: 1 }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 0.5,
          color: "text.secondary",
          textTransform: "capitalize",
        }}
      >
        {page.contentType || "unknown"}{" "}
      </Typography>{" "}
      {isAdmin && (
        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <InputLabel>Page type</InputLabel>{" "}
          <Select
            value={page.contentType || "unknown"}
            label="Page type"
            onChange={(e) =>
              onContentTypeChange(page.pageNumber, e.target.value)
            }
          >
            <MenuItem value="printed">Printed</MenuItem>{" "}
            <MenuItem value="handwritten">Handwritten</MenuItem>{" "}
            <MenuItem value="unknown">Unknown</MenuItem>{" "}
          </Select>{" "}
        </FormControl>
      )}{" "}
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        <Button
          size="small"
          variant="text"
          startIcon={saved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          onClick={toggleSaved}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {saved ? "Saved for Later" : "Save for Later"}
        </Button>

        <Button
          variant="contained"
          onClick={() =>
            navigate(`/document/${documentId}/page/${page.pageNumber}`)
          }
          sx={{
            textTransform: "none",
            minWidth: "120px",
          }}
        >
          View Page
        </Button>
      </Box>
    </Box>
  );
};

const TranscriptionPageViewer = () => {
  const { documentId } = useParams();
  const [document, setDocument] = useState(null);
  const [filteredPages, setFilteredPages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [contentTypeFilter, setContentTypeFilter] = useState("all");

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        setDocument(res.data);
      })
      .catch((err) => console.error("Error fetching documents:", err));
  }, [documentId]);

  useEffect(() => {
    applyFilters(statusFilter, contentTypeFilter, document);
  }, [document, statusFilter, contentTypeFilter]);

  const applyFilters = (status, contentType, doc) => {
    if (!doc?.pages) {
      setFilteredPages([]);
      return;
    }

    let pages = [...doc.pages];

    if (status !== "all") {
      pages = pages.filter((page) => page.status === status);
    }

    if (contentType !== "all") {
      pages = pages.filter(
        (page) => (page.contentType || "unknown") === contentType
      );
    }

    setFilteredPages(pages);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  const handleContentTypeFilterChange = (event) => {
    setContentTypeFilter(event.target.value);
  };

  const handleDownloadZip = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/documents/${documentId}/export-zip`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type: "application/zip",
      });

      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${
        document?.title?.replace(/[^\w\-]+/g, "_") || "transcription_export"
      }.zip`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download zip export failed:", err);
      alert("Failed to download transcription + image export.");
    }
  };
  const handleAdminContentTypeChange = async (pageNumber, contentType) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/content-type`,
        { contentType },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      const updatedPages =
        document?.pages?.map((page) =>
          page.pageNumber === pageNumber
            ? { ...page, contentType, contentTypeReviewedByAdmin: true }
            : page
        ) || [];

      const updatedDocument = { ...document, pages: updatedPages };
      setDocument(updatedDocument);

      // re-apply current filters
      applyFilters(statusFilter, contentTypeFilter, updatedDocument);
    } catch (err) {
      console.error("Failed to update page content type", err);
      alert(err.response?.data?.error || "Failed to update page type");
    }
  };

  return (
    <Box
      sx={{
        width: "auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/transcription`)}
          sx={{
            color: "#111111",
            backgroundColor: "#fff7db",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: "#fff1bf",
            },
          }}
        >
          View Documents
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<HistoryOutlinedIcon />}
            onClick={() => navigate("/transcription/my-activity")}
            sx={{
              minHeight: 44,
              color: "#3f3a20",
              borderColor: "#d8cfaa",
              backgroundColor: "#fff",

              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,

              "&:hover": {
                backgroundColor: "#fff7db",
                borderColor: "#c5b879",
              },

              "&:focus-visible": {
                outline: "3px solid #786d3e",
                outlineOffset: "2px",
              },
            }}
          >
            My Activity
          </Button>

          {user?.role === "admin" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                bgcolor: "#f5f5f5",
                border: "1px dashed #bdbdbd",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Admin tools:
              </Typography>

              <Button
                variant="text"
                size="small"
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleDownloadZip}
                sx={{ textTransform: "none", px: 0.5 }}
              >
                Export .zip
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <FloatingInstructions />

      <Typography
        variant="h4"
        sx={{
          marginBottom: "30px",
          textAlign: "center",
          paddingBottom: "5px",
        }}
      >
        {document?.title}
      </Typography>

      {document && <ProgressBar documents={[document]} />}

      <Typography variant="body1" sx={{ marginBottom: "20px" }}>
        {document?.description}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        {/* LEFT: Progress */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Filter by progress
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {[
              "all",
              "not started",
              "in progress",
              "in review",
              "completed",
            ].map((status) => {
              const isActive = statusFilter === status;

              const labelMap = {
                all: "All",
                "not started": "Not started",
                "in progress": "In progress",
                "in review": "In review",
                completed: "Completed",
              };

              return (
                <Button
                  key={status}
                  onClick={() => handleStatusFilterChange(status)}
                  variant={isActive ? "contained" : "outlined"}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 2,
                    py: 0.6,
                    fontWeight: 700,
                    fontSize: "0.85rem",

                    color: "#111111",
                    backgroundColor: isActive ? "#facc15" : "#ffffff",
                    border: isActive
                      ? "1px solid #eab308"
                      : "1px solid #e5e7eb",

                    "&:hover": {
                      backgroundColor: isActive ? "#eab308" : "#f3f4f6",
                    },
                  }}
                >
                  {labelMap[status]}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT: Page Type */}
        <Box
          sx={{
            width: 220,
            minWidth: 200,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Filter by page type
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Page type</InputLabel>
            <Select
              value={contentTypeFilter}
              label="Page type"
              onChange={handleContentTypeFilterChange}
            >
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="printed">Printed</MenuItem>
              <MenuItem value="handwritten">Handwritten</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </Select>
          </FormControl>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 3 }}
          >
            Printed = typed text, Handwritten = manuscript-style text
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
            lg: "1fr 1fr 1fr 1fr 1fr",
          },
          gap: "20px",
          alignItems: { xs: "center" },
          justifyItems: { xs: "center" },
          textAlign: { xs: "center" },
        }}
      >
        {filteredPages.length > 0 ? (
          filteredPages.map((page) => (
            <PageItem
              key={page.pageNumber}
              page={page}
              documentId={documentId}
              navigate={navigate}
              isAdmin={user?.role === "admin"}
              onContentTypeChange={handleAdminContentTypeChange}
            />
          ))
        ) : (
          <Typography>No pages found.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default TranscriptionPageViewer;
